import {
  Component,
  TemplateRef,
  ViewChild,
  OnInit,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { Inputfiledsbase } from '../../../inputfileds/models/inputfiledsbase';
import { QuestionControlService } from '../../../inputfileds/form_services/question-control.service';
import { EcontentServicesService } from '../../../inputfileds/form_services/econtent-services/econtent-services.service';
import { ApiservicesService } from '../../../Services/Apis/apiservices.service';
import { DynamicFormQuestionComponent } from '../../../inputfileds/dynamic-form-question/dynamic-form-question.component';

@Component({
    selector: 'app-econtent-documents-data',
    imports: [
        CommonModule,
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        MatTableModule,
        MatDialogModule,
        ReactiveFormsModule,
        DynamicFormQuestionComponent,
    ],
    providers: [QuestionControlService],
    templateUrl: './econtent-documents-data.component.html',
    styleUrls: ['./econtent-documents-data.component.scss']
})
export class EcontentDocumentsDataComponent implements OnInit {
  coursesId!: string;
  selectedSemester!: string;
  selectedSubject!: string;
  documents: any[] = [];
  questions: Inputfiledsbase<string>[] = [];
  fileToUpload: File | null = null;
  dialogRef!: MatDialogRef<any>;
  filePreview: string | null = null;
  fileName: string | null = null;
  isImage: boolean = false;
  @Input() question!: Inputfiledsbase<string>;
  @Input() form!: FormGroup;

  // Document viewer properties
  isViewingDocument: boolean = false;
  currentDocumentUrl: SafeResourceUrl | null = null;
  currentDocumentType: string | null = null;
  currentDocument: any = null;

  displayedColumns: string[] = [
    'title',
    'type',
    'size',
    'uploadedDate',
    'uploadedBy',
    'status',
    'actions',
  ];

  constructor(
    private eContentFormQuestions: EcontentServicesService,
    private qcs: QuestionControlService,
    private apiServices: ApiservicesService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.coursesId = params.get('courseId')!;
      this.selectedSemester = params.get('semesterId')!;
      this.selectedSubject = params.get('subjectId')!;
      this.loadDocuments();
    });

    this.eContentFormQuestions.showecontentdocform().subscribe((questions) => {
      this.questions = questions;
      this.form = this.qcs.toFormGroup(questions);
    });
  }

  loadDocuments(): void {
    this.apiServices
      .getEcontentSubjectDocList(
        this.coursesId,
        this.selectedSemester,
        this.selectedSubject
      )
      .subscribe({
        next: (res: any) => {
          this.documents = res?.data ?? [];
        },
        error: (err) => console.error(err),
      });
  }

  openAddDialog(templateRef: TemplateRef<any>): void {
    this.dialogRef = this.dialog.open(templateRef, { width: '600px' });
  }

  closeDialog(): void {
    this.dialogRef?.close();
    this.resetFileUpload();
  }

  handleAction(action: string, documentId: number): void {
    switch (action) {
      case 'view':
        this.viewDocument(documentId);
        break;
      case 'edit':
        this.editDocument(documentId);
        break;
      case 'delete':
        this.deleteDocument(documentId);
        break;
    }
  }

  viewDocument(documentId: number): void {
    this.currentDocument = this.documents.find((doc) => doc.id === documentId);
    if (!this.currentDocument || !this.currentDocument.url) {
      console.warn('Document or URL not found');
      return;
    }

    this.currentDocumentType = this.getDocumentType(this.currentDocument.url);
    const fullUrl = this.getFullDocumentUrl(this.currentDocument.url);

    if (this.currentDocumentType === 'pdf') {
      this.currentDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://docs.google.com/gview?url=${encodeURIComponent(
          fullUrl
        )}&embedded=true`
      );
    } else if (
      this.currentDocumentType === 'word' ||
      this.currentDocumentType === 'excel' ||
      this.currentDocumentType === 'powerpoint'
    ) {
      this.currentDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          fullUrl
        )}`
      );
    } else if (this.currentDocumentType === 'image') {
      this.currentDocumentUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
    }

    this.isViewingDocument = true;
  }

  backToList(): void {
    this.isViewingDocument = false;
    this.currentDocumentUrl = null;
    this.currentDocumentType = null;
    this.currentDocument = null;
  }

  downloadDocument(): void {
    if (!this.currentDocument?.url) return;

    const fullUrl = this.getFullDocumentUrl(this.currentDocument.url);
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = this.currentDocument.title || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getDocumentType(url: any): string {
    if (!url) return 'unknown';

    const extension = url.split('.').pop()?.toLowerCase() || '';

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return 'image';
    } else if (extension === 'pdf') {
      return 'pdf';
    } else if (['doc', 'docx'].includes(extension)) {
      return 'word';
    } else if (['xls', 'xlsx'].includes(extension)) {
      return 'excel';
    } else if (['ppt', 'pptx'].includes(extension)) {
      return 'powerpoint';
    } else {
      return 'unknown';
    }
  }

  private getFullDocumentUrl(relativeUrl: string): string {
    if (!relativeUrl) return '';

    if (relativeUrl.startsWith('http')) {
      return relativeUrl;
    }

    return `${this.apiServices.environmentPath}/${relativeUrl}`;
  }

  editDocument(documentId: number): void {
    console.log('Editing document with ID:', documentId);
  }

  deleteDocument(documentId: number): void {
    this.apiServices.deleteEContentDocumentData(documentId).subscribe((res) => {
      if (res.code === '202') {
        this.loadDocuments();
      } else {
        console.warn(res.message);
      }
    });
  }

  resetFileUpload(): void {
    this.filePreview = null;
    this.fileName = null;
    this.isImage = false;
  }

  onSubmit(): void {
    if (this.form.valid) {
      const formData = new FormData();

      formData.append('title', this.form.value['econtent_title']);
      formData.append('course_id', this.coursesId);
      formData.append('semester_id', this.selectedSemester);
      formData.append('subject_id', this.selectedSubject);
      formData.append('category_id', this.documents[0]?.category_id || 195);

      if (this.fileToUpload) {
        formData.append('econtent_media', this.fileToUpload);
      }

      this.apiServices.createEContentData(formData).subscribe({
        next: (res: any) => {
          if (res.code === 201) {
            this.loadDocuments();
            this.closeDialog();
          } else {
            console.warn('Unexpected response code:', res.message);
          }
        },
        error: (err) => console.error('API Error:', err),
      });
    } else {
      console.warn('Form is invalid');
    }
  }

  getFileTypeIcon(fileType: string): string {
    if (!fileType) return 'insert_drive_file';

    const type = fileType.toLowerCase();

    if (type.includes('pdf')) return 'picture_as_pdf';
    if (type.includes('word') || type.includes('doc')) return 'description';
    if (type.includes('excel') || type.includes('xls')) return 'table_chart';
    if (type.includes('powerpoint') || type.includes('ppt')) return 'slideshow';
    if (
      type.includes('image') ||
      type.includes('jpg') ||
      type.includes('jpeg') ||
      type.includes('png') ||
      type.includes('gif')
    )
      return 'image';
    if (type.includes('text') || type.includes('txt')) return 'text_snippet';
    if (type.includes('video')) return 'videocam';
    if (type.includes('audio')) return 'audiotrack';
    if (type.includes('zip') || type.includes('rar') || type.includes('7z'))
      return 'folder_zip';

    return 'insert_drive_file';
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fileToUpload = input.files[0];
      this.fileName = this.fileToUpload.name;
      this.isImage = this.fileToUpload.type.startsWith('image/');
      if (this.isImage) {
        const reader = new FileReader();
        reader.onload = () => {
          this.filePreview = reader.result as string;
        };
        reader.readAsDataURL(this.fileToUpload);
      } else {
        this.filePreview = null;
      }
    }
  }

  navigateToBackPage(): void {
    window.history.back();
  }
}
